import {
  Tournament,
  TournamentCompetitionDefinition,
  TournamentMatchSource,
  TournamentStructureMatch
} from 'Src/models/tournament';
import { structureId } from './structureId';

export const BRACKET_ROUNDS = [
  { id: 'ROUND_OF_32', label: 'Round of 32 · 1/16 final', slots: 32 },
  { id: 'ROUND_OF_16', label: 'Round of 16 · 1/8 final', slots: 16 },
  { id: 'QUARTER_FINAL', label: 'Quarter-finals', slots: 8 },
  { id: 'SEMI_FINAL', label: 'Semi-finals', slots: 4 },
  { id: 'FINAL', label: 'Final', slots: 2 }
];

export function roundRobinPairs(count: number): Array<[number, number]> {
  if (count === 4)
    return [
      [0, 1],
      [2, 3],
      [0, 2],
      [1, 3],
      [0, 3],
      [1, 2]
    ];
  const pairs: Array<[number, number]> = [];
  for (let home = 0; home < count; home++) for (let away = home + 1; away < count; away++) pairs.push([home, away]);
  return pairs;
}

export function roundRobinFixtures(
  groupId: string,
  name: string,
  sources: TournamentMatchSource[],
  startTime: string,
  duration: number,
  pause: number
): TournamentStructureMatch[] {
  return roundRobinPairs(sources.length)
    .filter(([home, away]) => sources[home].sourceType !== 'BYE' || sources[away].sourceType !== 'BYE')
    .map(([home, away], index) => {
      const start = +new Date(startTime) + index * (duration + pause) * 60000;
      return {
        id: structureId(),
        generated: true,
        groupId,
        name: `${name} · Round robin ${index + 1}`,
        round: 'GROUP',
        homeSource: sources[home],
        awaySource: sources[away],
        startTime: new Date(start).toISOString(),
        endTime: new Date(start + duration * 60000).toISOString()
      };
    });
}

export function knockoutFixtures(
  groupId: string,
  name: string,
  firstRound: string,
  slots: TournamentMatchSource[],
  startTime: string,
  duration: number,
  pause: number,
  pitchId?: string
): TournamentStructureMatch[] {
  const offset = BRACKET_ROUNDS.findIndex(round => round.id === firstRound);
  if (offset < 0 || slots.length !== BRACKET_ROUNDS[offset].slots)
    throw new Error('Choose every entrant for the selected first round.');
  const fixtures: TournamentStructureMatch[] = [];
  let entrants = slots;
  BRACKET_ROUNDS.slice(offset).forEach((round, roundIndex) => {
    const winners: TournamentMatchSource[] = [];
    for (let index = 0; index < entrants.length; index += 2) {
      if (entrants[index].sourceType === 'BYE' && entrants[index + 1].sourceType === 'BYE')
        throw new Error('Two byes cannot play each other. Move one bye to another pairing.');
      const id = structureId();
      const start = +new Date(startTime) + roundIndex * (duration + pause) * 60000;
      fixtures.push({
        id,
        groupId,
        generated: true,
        name: `${name} · ${round.label} ${index / 2 + 1}`,
        round: round.id,
        homeSource: entrants[index],
        awaySource: entrants[index + 1],
        startTime: new Date(start).toISOString(),
        endTime: new Date(start + duration * 60000).toISOString(),
        pitchId
      });
      winners.push({ sourceType: 'MATCH_WINNER', sourceMatchId: id });
    }
    entrants = winners;
  });
  return fixtures;
}

export function resizeKnockoutFixtures(
  matches: TournamentStructureMatch[],
  groupId: string,
  name: string,
  firstRound: string,
  startTime: string,
  duration: number,
  pause: number
): TournamentStructureMatch[] {
  const old = matches
    .filter(match => match.groupId === groupId)
    .map(match => (match.round === 'ROUND_OF_8' ? { ...match, round: 'QUARTER_FINAL' } : match));
  const oldRound = BRACKET_ROUNDS.find(round => old.some(match => !match.custom && match.round === round.id));
  const entrants = old
    .filter(match => !match.custom && match.round === oldRound?.id)
    .reduce((all, match) => all.concat([match.homeSource, match.awaySource]), [] as TournamentMatchSource[]);
  const count = BRACKET_ROUNDS.find(round => round.id === firstRound)!.slots;
  const slots = Array.from(
    { length: count },
    (_, index) => entrants[index] || { sourceType: 'TEAM' as const, label: `Empty slot ${index + 1}` }
  );
  const fresh = knockoutFixtures(groupId, name, firstRound, slots, startTime, duration, pause);
  const idMap = new Map<string, string>();
  const indices: Record<string, number> = {};
  const oldByNew = new Map<string, TournamentStructureMatch>();
  fresh.forEach(match => {
    const index = indices[match.round!] || 0;
    indices[match.round!] = index + 1;
    const previous = old.filter(item => !item.custom && item.round === match.round)[index];
    if (previous?.id) {
      idMap.set(match.id!, previous.id);
      oldByNew.set(match.id!, previous);
    }
  });
  const ref = (source: TournamentMatchSource) =>
    source.sourceMatchId
      ? { ...source, sourceMatchId: idMap.get(source.sourceMatchId) || source.sourceMatchId }
      : source;
  return [
    ...fresh.map(match => {
      const previous = oldByNew.get(match.id!);
      return {
        ...match,
        id: idMap.get(match.id!) || match.id,
        eventId: previous?.eventId,
        homeSource: ref(match.homeSource),
        awaySource: ref(match.awaySource)
      };
    }),
    ...old.filter(match => match.custom || match.round === 'PLAYOFF')
  ];
}

export function applyGeneratedDefaults(
  definition: TournamentCompetitionDefinition,
  tournament: Tournament
): TournamentCompetitionDefinition {
  const groups = definition.layout?.groups || tournament.customGroups || [];
  const stages = definition.layout?.stages || tournament.tournamentLayout || [];
  const defaults = definition.scheduleDefaults || {
    groupDuration: 20,
    groupBreak: 5,
    knockoutDuration: 20,
    knockoutBreak: 5
  };
  return {
    ...definition,
    matches: definition.matches.map(match => {
      if (!match.generated) return match;
      const group = groups.find(item => item.id === match.groupId);
      const stage = stages.find(item => item.id === group?.stageId);
      const rr = match.round === 'GROUP';
      const peers = definition.matches.filter(item => item.groupId === match.groupId && !item.custom);
      const usedRounds = BRACKET_ROUNDS.filter(round => peers.some(item => item.round === round.id));
      const index = rr
        ? peers.findIndex(item => item.id === match.id)
        : Math.max(
            0,
            usedRounds.findIndex(round => round.id === match.round)
          );
      const duration =
        match.durationOverride && match.startTime && match.endTime
          ? Math.round((+new Date(match.endTime) - +new Date(match.startTime)) / 60000)
          : rr
          ? defaults.groupDuration
          : defaults.knockoutDuration;
      const start =
        match.timeOverride && match.startTime
          ? +new Date(match.startTime)
          : +new Date(stage?.startTime || tournament.startTime) +
            index *
              ((rr ? defaults.groupDuration : defaults.knockoutDuration) +
                (rr ? defaults.groupBreak : defaults.knockoutBreak)) *
              60000;
      const groupIndex = groups
        .filter(item => item.stageId === group?.stageId)
        .findIndex(item => item.id === group?.id);
      const pitchId = match.pitchOverride
        ? match.pitchId
        : group?.pitchMode === 'NONE'
        ? undefined
        : group?.pitchId || tournament.pitches?.[groupIndex % (tournament.pitches?.length || 1)]?.id;
      if (!Number.isFinite(start) || !Number.isFinite(duration)) return match;
      return {
        ...match,
        startTime: new Date(start).toISOString(),
        endTime: new Date(start + duration * 60000).toISOString(),
        pitchId
      };
    })
  };
}
